import i18n from "./i18n.ts";

// N4-2 success feedback helpers. Successful trade / sign / re-sign operations
// used to be fully silent — only failures surfaced a red toast, so the user
// could not perceive that the action landed. These pure builders encode both
// the success gate and the localized L1-toast text. Each returns `undefined`
// when the operation did NOT succeed, so callers fire a toast iff a string
// comes back (the failure path keeps its existing red toast untouched).

// A proposed trade only completes when the AI accepts it. `partnerName` and the
// acquired player names make the toast specific ("...acquired A, B") rather than
// a generic "operation succeeded".
export const getTradeSuccessToastText = ({
	accepted,
	partnerName,
	acquiredNames,
}: {
	accepted: boolean;
	partnerName: string;
	acquiredNames: string[];
}): string | undefined => {
	if (!accepted) {
		return undefined;
	}

	if (acquiredNames.length > 0) {
		return i18n.t("trade.successToastAcquired", {
			ns: "ui",
			defaultValue: "Trade completed with {{team}} — acquired {{players}}",
			team: partnerName,
			players: acquiredNames.join(", "),
		});
	}

	return i18n.t("trade.successToast", {
		ns: "ui",
		defaultValue: "Trade completed with {{team}}",
		team: partnerName,
	});
};

// `acceptContractNegotiation` returns an error string on failure and `undefined`
// on success. `resigning` picks the re-sign vs. free-agent-signing wording; the
// name + years + formatted amount make the toast concrete.
export const getSignSuccessToastText = ({
	errorMessage,
	resigning,
	playerName,
	years,
	amountText,
}: {
	errorMessage: string | undefined;
	resigning: boolean;
	playerName: string;
	years: number;
	amountText: string;
}): string | undefined => {
	if (errorMessage !== undefined) {
		return undefined;
	}

	return resigning
		? i18n.t("negotiation.reSignToast", {
				ns: "ui",
				defaultValue: "Re-signed {{name}} ({{years}} yr, {{amount}}/yr)",
				name: playerName,
				years,
				amount: amountText,
			})
		: i18n.t("negotiation.signToast", {
				ns: "ui",
				defaultValue: "Signed {{name}} ({{years}} yr, {{amount}}/yr)",
				name: playerName,
				years,
				amount: amountText,
			});
};

// R7-1: submitting the expansion-draft protection list ("Start Expansion
// Draft") used to be silent — N4-2 only covered trade / sign / re-sign. The
// worker's `startExpansionDraft` commits the list and resolves without an error
// signal (the only rejection path is the upstream confirm dialogs), so this
// builder returns the localized toast text unconditionally; the caller fires it
// once the worker call resolves.
export const getProtectPlayersSuccessToastText = ({
	protectedCount,
}: {
	protectedCount: number;
}): string =>
	i18n.t("protectPlayers.submitSuccessToast", {
		ns: "ui",
		count: protectedCount,
		defaultValue: "Protection list submitted — {{count}} players protected",
	});

// `reSignAll` returns an error string on failure and `undefined` when every
// player was re-signed.
export const getReSignAllSuccessToastText = ({
	errorMsg,
	count,
}: {
	errorMsg: string | undefined;
	count: number;
}): string | undefined => {
	if (errorMsg) {
		return undefined;
	}

	return i18n.t("negotiationList.reSignAllToast", {
		ns: "ui",
		defaultValue: "Re-signed {{count}} players",
		count,
	});
};
